import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Top-level error boundary that prevents uncaught React render errors from
 * white-screening the whole app. The full error (name + message + stack +
 * React component stack) is rendered inline in the same fallback view so a
 * developer or support user can read it, copy it, and start fixing without
 * having to open devtools or expand a hidden section.
 *
 * Wrap the root <App /> in <ErrorBoundary>...</ErrorBoundary>. Nested
 * boundaries are useful around route-level chunks if a single broken page
 * shouldn't crash the inbox.
 *
 * Uses raw HTML elements + Tailwind utility classes with --heltar-* CSS
 * variables (rather than @heltarchat/ui components) so that if the UI
 * package itself crashes, the boundary's fallback still renders.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ errorInfo: info });
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback && this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }

    const { error, errorInfo } = this.state;

    return (
      <div
        role='alert'
        className='flex min-h-[var(--app-height,100vh)] flex-col items-center justify-center bg-[var(--heltar-bg-surface)] p-6 text-[var(--heltar-text-primary)]'
      >
        <div className='flex w-full max-w-2xl flex-col gap-4'>
          <h1 className='text-2xl font-semibold'>Something went wrong</h1>
          <p className='text-sm text-[var(--heltar-text-secondary)]'>
            The page hit an unexpected error and stopped responding. The full
            error is shown below — copy it when reporting the issue or use the
            details to debug in place.
          </p>

          {error && (
            <pre className='max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-md border border-[var(--heltar-border-default)] bg-[var(--heltar-bg-surface-elevated)] p-4 text-xs leading-relaxed'>
              <span className='mb-2 block font-semibold text-[var(--heltar-state-error)]'>
                {error.name}: {error.message}
              </span>
              {error.stack && (
                <span className='block opacity-80'>{error.stack}</span>
              )}
              {errorInfo?.componentStack && (
                <span className='mt-3 block border-t border-[var(--heltar-border-subtle)] pt-3 opacity-80'>
                  Component stack:{errorInfo.componentStack}
                </span>
              )}
            </pre>
          )}

          <div className='flex gap-3'>
            <button
              type='button'
              onClick={this.reset}
              className='cursor-pointer rounded-md border-none bg-[var(--heltar-brand-primary)] px-5 py-2 text-sm font-medium text-white hover:bg-[var(--heltar-brand-primary-hover)]'
            >
              Try again
            </button>
            <button
              type='button'
              onClick={() => window.location.reload()}
              className='cursor-pointer rounded-md border border-[var(--heltar-border-default)] bg-transparent px-5 py-2 text-sm font-medium text-[var(--heltar-text-primary)] hover:border-[var(--heltar-border-button-hover)]'
            >
              Refresh page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
